import React from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { UserInvitation } from '../../types/organization.types';
import { useResendInvitation } from '../../hooks/useUserInvitations';
import { format, isAfter } from 'date-fns';
import { Mail, Clock } from 'lucide-react';
import { StatusBadge } from '@/components/status/StatusBadge';

export interface InvitationsTableProps {
  invitations: UserInvitation[];
  isLoading: boolean;
}

export const InvitationsTable: React.FC<InvitationsTableProps> = ({ invitations, isLoading }) => {
  const resendInvitation = useResendInvitation();

  const canResend = (invitation: UserInvitation) => {
    return invitation.status === 'pending' && isAfter(new Date(invitation.expires_at), new Date());
  };

  const getStatusBadge = (invitation: UserInvitation) => {
    const isExpired = invitation.status === 'pending' && isAfter(new Date(), new Date(invitation.expires_at));
    
    if (isExpired) {
      return <StatusBadge status="expired" label="Expired" />;
    }
    
    switch (invitation.status) {
      case 'pending':
        return <StatusBadge status="pending" />;
      case 'accepted':
        return <StatusBadge status="accepted" />;
      case 'cancelled':
        return <StatusBadge status="cancelled" />;
      default:
        return <StatusBadge status={invitation.status} />;
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading invitations...</div>;
  }

  if (invitations.length === 0) {
    return (
      <div className="text-center py-8">
        <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No invitations sent</h3>
        <p className="text-gray-500">Pending invitations will appear here.</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Invited</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invitations.map((invitation) => (
            <TableRow key={invitation.id}>
              <TableCell className="font-medium">{invitation.email}</TableCell>
              <TableCell>
                <Badge variant="secondary">
                  {invitation.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </TableCell>
              <TableCell>{getStatusBadge(invitation)}</TableCell>
              <TableCell>
                {format(new Date(invitation.invited_at), 'MMM dd, yyyy')}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {format(new Date(invitation.expires_at), 'MMM dd, yyyy')}
                </div>
              </TableCell>
              <TableCell>
                {canResend(invitation) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resendInvitation.mutate(invitation.id)}
                    disabled={resendInvitation.isPending}
                  >
                    {resendInvitation.isPending ? 'Resending...' : 'Resend'}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default InvitationsTable;
